import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import { API_URL } from "../../utils/api-config";

interface FuelCard {
  _id: string;
  cardNumber: string;
  provider: string;
}

interface Driver {
  _id: string;
  firstName: string;
  lastName: string;
  driverId: string;
}

export default function FuelCardAssignDriver() {
  const [cardNumber, setCardNumber] = useState("");
  const [driverId, setDriverId] = useState("");
  const [secondaryCardNumber, setSecondaryCardNumber] = useState("");
  const [secondaryDriverId, setSecondaryDriverId] = useState("");
  const [loading, setLoading] = useState(false);
  const [secondaryLoading, setSecondaryLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [secondarySuccess, setSecondarySuccess] = useState<string | null>(null);
  const [secondaryError, setSecondaryError] = useState<string | null>(null);
  const [cards, setCards] = useState<FuelCard[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoadingData(true);
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        
        // Récupérer la liste des cartes
        const cardsRes = await fetch(`${API_URL}/fuel-cards`, { headers });
        const cardsData = await cardsRes.json();
        setCards(Array.isArray(cardsData) ? cardsData : []);

        // Récupérer la liste des chauffeurs
        const driversRes = await fetch(`${API_URL}/drivers`, { headers });
        const driversData = await driversRes.json();
        setDrivers(Array.isArray(driversData) ? driversData : []);
      } catch (err) {
        console.error("Erreur lors du chargement des données", err);
        setError("Erreur lors du chargement des données");
        setCards([]);
        setDrivers([]);
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
  }, []);

  const handleCardChange = (event: SelectChangeEvent) => {
    setCardNumber(event.target.value);
  };

  const handleDriverChange = (event: SelectChangeEvent) => {
    setDriverId(event.target.value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cardNumber || !driverId) {
      setError("Veuillez sélectionner une carte et un chauffeur");
      return;
    }

    setLoading(true);
    setSuccess(null);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_URL}/fuel-cards/${cardNumber}/assign-driver`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({ driverId }),
        }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.message || "Erreur lors de l'association");
      }
      setSuccess("Carte associée au chauffeur avec succès !");
      setCardNumber("");
      setDriverId("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de l'association de la carte au chauffeur");
    } finally {
      setLoading(false);
    }
  };

  const handleSecondarySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!secondaryCardNumber || !secondaryDriverId) {
      setSecondaryError("Veuillez sélectionner une carte et un chauffeur");
      return;
    }

    setSecondaryLoading(true);
    setSecondarySuccess(null);
    setSecondaryError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_URL}/fuel-cards/${secondaryCardNumber}/assign-driver`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({ driverId: secondaryDriverId, isSecondary: true }),
        }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.message || "Erreur lors de l'association");
      }
      setSecondarySuccess("Carte complémentaire associée au chauffeur avec succès !");
      setSecondaryCardNumber("");
      setSecondaryDriverId("");
    } catch (err) {
      setSecondaryError(err instanceof Error ? err.message : "Erreur lors de l'association de la carte complémentaire");
    } finally {
      setSecondaryLoading(false);
    }
  };

  if (loadingData) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 4, maxWidth: 900, mx: "auto" }}>
      {/* Carte principale */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ flex: 1, maxWidth: 400 }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Associer une carte à un chauffeur
        </Typography>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="card-select-label">Carte</InputLabel>
          <Select
            labelId="card-select-label"
            value={cardNumber}
            label="Carte"
            onChange={handleCardChange}
            required
          >
            {cards.map((card) => (
              <MenuItem key={card._id} value={card.cardNumber}>
                {card.cardNumber} - {card.provider}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="driver-select-label">Chauffeur</InputLabel>
          <Select
            labelId="driver-select-label"
            value={driverId}
            label="Chauffeur"
            onChange={handleDriverChange}
            required
          >
            {drivers.map((driver) => (
              <MenuItem key={driver._id} value={driver.driverId}>
                {driver.firstName} {driver.lastName} ({driver.driverId})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : "Associer"}
        </Button>
      </Box>

      {/* Carte complémentaire */}
      <Box
        component="form"
        onSubmit={handleSecondarySubmit}
        sx={{ flex: 1, maxWidth: 400 }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Associer une carte complémentaire à un chauffeur
        </Typography>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="secondary-card-select-label">Carte complémentaire</InputLabel>
          <Select
            labelId="secondary-card-select-label"
            value={secondaryCardNumber}
            label="Carte complémentaire"
            onChange={(e: SelectChangeEvent) => setSecondaryCardNumber(e.target.value)}
            required
          >
            {cards.map((card) => (
              <MenuItem key={card._id} value={card.cardNumber}>
                {card.cardNumber} - {card.provider}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="secondary-driver-select-label">Chauffeur</InputLabel>
          <Select
            labelId="secondary-driver-select-label"
            value={secondaryDriverId}
            label="Chauffeur"
            onChange={(e: SelectChangeEvent) => setSecondaryDriverId(e.target.value)}
            required
          >
            {drivers.map((driver) => (
              <MenuItem key={driver._id} value={driver.driverId}>
                {driver.firstName} {driver.lastName} ({driver.driverId})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {secondarySuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {secondarySuccess}
          </Alert>
        )}
        {secondaryError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {secondaryError}
          </Alert>
        )}
        <Button
          type="submit"
          variant="contained"
          color="secondary"
          fullWidth
          disabled={secondaryLoading}
        >
          {secondaryLoading ? <CircularProgress size={24} /> : "Associer carte complémentaire"}
        </Button>
      </Box>
    </Box>
  );
}
